import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Star, Clock, Phone, Flame, UserCheck, KanbanSquare, BarChart3 } from 'lucide-react';
import { useAuth } from '../../lib/auth.js';
import { AgentDashboard } from '../../components/dashboard/AgentDashboard.js';
import { ManagerDashboard } from '../../components/dashboard/ManagerDashboard.js';
import { AdminDashboard } from '../../components/dashboard/AdminDashboard.js';
import { Skeleton } from '../../components/ui/Skeleton.js';
import { statsApi, buildingsApi, areasApi } from '../../lib/api.js';
import { roleAtLeast } from '@imza/shared';

export const Route = createFileRoute('/_auth/')({
  component: DashboardPage,
});

function KpiCard({
  label,
  value,
  subtitle,
  loading,
  delay = 0,
}: {
  label: string;
  value: string;
  subtitle: string;
  loading: boolean;
  delay?: number;
}) {
  return (
    <div
      className="bg-white border border-border rounded-2xl p-5 animate-fade-in-up"
      style={{ animationDelay: `${delay}ms`, animationFillMode: 'both' }}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wider text-secondary mb-2">{label}</p>
      {loading ? (
        <Skeleton className="h-10 w-24 mb-1" />
      ) : (
        <p className="text-4xl font-bold text-primary-text tabular-nums animate-count-up">{value}</p>
      )}
      <p className="text-xs text-secondary mt-1">{subtitle}</p>
    </div>
  );
}

function QuickActionCard({
  icon: Icon,
  title,
  subtitle,
  href,
  delay = 0,
}: {
  icon: React.ElementType;
  title: string;
  subtitle: string;
  href: string;
  delay?: number;
}) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate({ to: href as any })}
      className="flex items-center gap-3 bg-white border border-border rounded-xl px-4 py-3.5 hover:border-primary hover:shadow-md transition-all animate-fade-in text-left"
      style={{ animationDelay: `${delay}ms`, animationFillMode: 'both' }}
    >
      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-primary-text">{title}</p>
        <p className="text-xs text-secondary">{subtitle}</p>
      </div>
    </button>
  );
}

function DashboardPage() {
  const { user } = useAuth();

  const { data: overviewData, isLoading: overviewLoading } = useQuery({
    queryKey: ['stats', 'overview'],
    queryFn: () => statsApi.overview(),
    staleTime: 5 * 60_000,
  });

  const { data: buildingsData, isLoading: buildingsLoading } = useQuery({
    queryKey: ['stats', 'buildings-total'],
    queryFn: () => buildingsApi.listPaginated({ page: 1, pageSize: 1 }),
    staleTime: 5 * 60_000,
  });

  const { data: areasData, isLoading: areasLoading } = useQuery({
    queryKey: ['stats', 'areas-list'],
    queryFn: () => areasApi.list(),
    staleTime: 10 * 60_000,
  });

  if (!user) return null;

  const totalRecords = overviewData?.totalRecords ?? 0;
  const totalBuildings = buildingsData?.total ?? 0;
  const totalAreas = areasData?.items?.length ?? 0;

  const today = new Date();
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const dateStr = `${dayNames[today.getDay()]}, ${today.getDate()} ${monthNames[today.getMonth()]} ${today.getFullYear()}`;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      {/* Welcome header */}
      <div className="flex items-start justify-between mb-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-primary-text">
            Welcome back, {user.name?.split(' ')[0] || 'admin'} 👋
          </h1>
          <p className="text-sm text-secondary mt-1">Your property registry at a glance</p>
        </div>
        <p className="text-sm text-secondary flex items-center gap-1.5 pt-1">
          📅 {dateStr}
        </p>
      </div>

      {/* KPI Cards — 2 cols on mobile, 4 on desktop */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          label="Total Records"
          value={totalRecords.toLocaleString()}
          subtitle="Across the database"
          loading={overviewLoading}
          delay={0}
        />
        <KpiCard
          label="Buildings"
          value={totalBuildings.toLocaleString()}
          subtitle="Unique properties"
          loading={buildingsLoading}
          delay={100}
        />
        <KpiCard
          label="Areas"
          value={totalAreas.toLocaleString()}
          subtitle="Communities covered"
          loading={areasLoading}
          delay={200}
        />
        <KpiCard
          label="Follow-ups Due"
          value="0"
          subtitle="Today or overdue"
          loading={false}
          delay={300}
        />
      </div>

      {/* Pipeline Status */}
      <div
        className="bg-white border border-border rounded-2xl p-5 mb-6 animate-fade-in-up"
        style={{ animationDelay: '200ms', animationFillMode: 'both' }}
      >
        <h2 className="text-sm font-bold text-primary-text flex items-center gap-2 mb-4">
          <BarChart3 className="w-4 h-4 text-primary" aria-hidden="true" />
          Pipeline Status
        </h2>
        <p className="text-sm text-secondary text-center py-6">
          No CRM status data yet. Set statuses on records to track your pipeline.
        </p>
      </div>

      {/* Quick Actions */}
      <div
        className="bg-white border border-border rounded-2xl p-5 mb-6 animate-fade-in-up"
        style={{ animationDelay: '300ms', animationFillMode: 'both' }}
      >
        <h2 className="text-sm font-bold text-primary-text flex items-center gap-2 mb-4">
          ⚡ Quick Actions
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <QuickActionCard
            icon={Star}
            title="Starred (0)"
            subtitle="Hot leads you flagged"
            href="/landlords?is_starred=true"
            delay={350}
          />
          <QuickActionCard
            icon={Clock}
            title="Follow-ups (0)"
            subtitle="Due today or overdue"
            href="/landlords"
            delay={400}
          />
          <QuickActionCard
            icon={Phone}
            title="Never contacted"
            subtitle="Records with no call log"
            href="/landlords"
            delay={450}
          />
          <QuickActionCard
            icon={Flame}
            title="Hot pipeline"
            subtitle="Sale + Potential active"
            href="/landlords"
            delay={500}
          />
          <QuickActionCard
            icon={UserCheck}
            title="Assigned to me (0)"
            subtitle="Records you own"
            href="/landlords"
            delay={550}
          />
          <QuickActionCard
            icon={KanbanSquare}
            title="Pipeline board"
            subtitle="Kanban view of all stages"
            href="/kanban"
            delay={600}
          />
        </div>
      </div>

      {/* Role-specific dashboard section */}
      <div
        className="animate-fade-in-up"
        style={{ animationDelay: '400ms', animationFillMode: 'both' }}
      >
        {roleAtLeast(user.role, 'admin') ? (
          <AdminDashboard />
        ) : roleAtLeast(user.role, 'manager') ? (
          <ManagerDashboard />
        ) : (
          <AgentDashboard />
        )}
      </div>
    </div>
  );
}
